import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { of, switchMap } from 'rxjs';
import { NgClass } from '@angular/common';
import { LayoutService } from '../../layouts/layout.service';
import { InvestigationService } from '../../core/services/investigation.service';
import { IncidentService } from '../../core/services/incident.service';
import { DataElementService } from '../../core/services/data-element.service';
import { ToastService } from '../../core/services/toast.service';
import { ProgramService } from '../../core/services/program.service';
import { CategoryService } from '../../core/services/category.service';
import {
  CdvInvestigation,
  InvestigationDetail,
  InvestigationLinkedIncident,
  UpdateInvestigationPayload,
} from '../../shared/models/investigation.models';
import { IncidentStage } from '../../shared/models/incident.models';
import { CdeProgramItem } from '../../shared/models/program.models';
import { SelectComponent, SelectOption } from '../../shared/components/select/select';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker';
import { FileUploaderComponent } from '../../shared/components/file-uploader/file-uploader';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal';
import { PersonSelectComponent } from '../../shared/components/person-select/person-select';
import {
  MultiselectDropdownComponent,
  MultiselectOption,
} from '../../shared/components/multiselect-dropdown/multiselect-dropdown';
import { AuthService } from '../../auth/auth.service';
import { apiErrorMessage } from '../../shared/utils/api-error';

type CaseInfoField = 'noticeDate' | 'person' | 'program' | 'categories';

const STAGE_META: Record<IncidentStage, { name: string }> = {
  new: { name: 'Pending' },
  A: { name: 'Creation' },
  B: { name: 'Incident Detail' },
  C: { name: 'Follow-Up' },
  D: { name: 'Compliance & PQ' },
  closed: { name: 'Closed' },
};

@Component({
  selector: 'app-investigation-detail',
  imports: [
    NgClass,
    SelectComponent,
    DatePickerComponent,
    FileUploaderComponent,
    ConfirmModalComponent,
    MultiselectDropdownComponent,
    PersonSelectComponent,
  ],
  templateUrl: './investigation-detail.html',
})
export class InvestigationDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private layout = inject(LayoutService);
  private investigationService = inject(InvestigationService);
  private incidentService = inject(IncidentService);
  private dataElementService = inject(DataElementService);
  private toast = inject(ToastService);
  private programService = inject(ProgramService);
  private categoryService = inject(CategoryService);
  private auth = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  protected investigation = signal<InvestigationDetail | null>(null);

  // A saved finding closes the investigation; the API rejects any PUT after that
  // (InvestigationClosedCannotEdit).
  protected isClosed = computed(
    () => this.parseFinding(this.investigation()?.finding ?? null) !== null,
  );

  // Edit and Close share the same roles (Oversight+). Program Leadership only has Read.
  protected canEdit = computed(
    () => this.auth.canDo('Investigations', ['Edition']) && !this.isClosed(),
  );
  protected loading = signal(true);
  protected sidebarOpen = signal(false);
  protected activeIncidentIndex = signal(0);

  // Add-incident flow
  protected addingIncident = signal(false);
  protected newIncidentId = signal('');
  protected savingIncident = signal(false);
  protected incidentOptions = signal<SelectOption[]>([]);
  protected loadingIncidentOptions = signal(false);

  // Remove-incident flow
  protected incidentToRemove = signal<InvestigationLinkedIncident | null>(null);
  protected removingIncident = signal(false);
  // An investigation always needs at least one linked incident (required on create).
  protected canRemoveIncident = computed(
    () => this.canEdit() && (this.investigation()?.incidentInvestigations.length ?? 0) > 1,
  );

  protected saving = signal(false);

  // Case information (the fields entered when the investigation was created)
  protected localNoticeDate = signal('');
  protected localPersonId = signal('');
  protected localProgramId = signal('');
  protected localCategoryIds = signal<string[]>([]);

  private programOptions = signal<SelectOption[]>([]);
  protected categoryOptions = signal<MultiselectOption[]>([]);
  protected caseOptionsLoading = signal(false);
  private caseOptionsLoaded = false;

  protected programSelectOptions = computed(() =>
    this.withSavedOption(
      this.programOptions(),
      this.investigation()?.investigatingProgramId,
      this.investigation()?.investigatingProgramName,
    ),
  );

  protected localNarrative = signal('');
  protected localFollowup = signal('');
  protected localFinding = signal<number | null>(null);
  protected localFindingDate = signal<string | null>(null);

  // Case information is required by the spec (same fields as creation). Errors show after Save.
  protected saveAttempted = signal(false);
  protected caseInfoMissing = computed(
    (): Record<CaseInfoField, boolean> => ({
      noticeDate: !this.localNoticeDate(),
      person: !this.localPersonId(),
      program: !this.localProgramId(),
      categories: this.localCategoryIds().length === 0,
    }),
  );
  protected caseInfoValid = computed(() => !Object.values(this.caseInfoMissing()).some(Boolean));

  protected showCaseError(field: CaseInfoField): boolean {
    return this.saveAttempted() && this.caseInfoMissing()[field];
  }

  protected isComplete = computed(() => {
    const inv = this.investigation();
    if (!inv) return false;
    return !!(this.caseInfoValid() && this.localFinding() !== null && this.localFindingDate());
  });

  // Only System Administration can undo a close (PUT /investigations/{id}/reopen).
  protected canReopen = computed(
    () => this.isClosed() && this.auth.hasRole('System Administration'),
  );
  protected showReopenConfirm = signal(false);
  protected reopening = signal(false);

  // The API closes the investigation as soon as a finding is saved, complete or not.
  protected willClose = computed(() => this.localFinding() !== null);
  protected showCloseConfirm = signal(false);
  protected closeAttempted = signal(false);
  protected missingForClose = computed(() => {
    const missing: string[] = [];
    if (!this.localNoticeDate()) missing.push('date of notice');
    if (!this.localPersonId()) missing.push('person sending notice');
    if (!this.localProgramId()) missing.push('investigating program');
    if (!this.localCategoryIds().length) missing.push('allegation categories');
    if (!this.localFindingDate()) missing.push('date of determination');
    return missing;
  });
  protected findingLabel = computed(() => this.findingLabelFor(this.localFinding()));

  protected findingLabelFor(value: string | number | null): string {
    const finding = this.parseFinding(value);
    if (finding === 1) return 'Substantiated / Indicated';
    if (finding === 0) return 'Unsubstantiated / Unfounded';
    return 'none';
  }

  protected cdeItems = signal<CdeProgramItem[]>([]);
  protected cdeValues = signal<Map<string, Partial<CdvInvestigation>>>(new Map());

  protected sortedCdePrograms = computed(() =>
    [...this.cdeItems()].sort((a, b) => a.displayOrder - b.displayOrder),
  );

  protected mappedDocuments = computed(() =>
    (this.investigation()?.documents ?? []).map((doc) => ({
      id: doc.id,
      name: doc.name,
      type: doc.documentType,
      documentUrl: doc.documentUrl,
    })),
  );

  /** List items in the order the admin set (display order), not alphabetical. */
  protected listItemsToOptions(
    items?: { id: string; value: string; displayOrder?: number | null }[],
  ): SelectOption[] {
    return (items ?? [])
      .slice()
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      .map((i) => ({ value: i.id, label: i.value }));
  }

  protected onCdeChange(
    cdeId: string,
    dataType: string | null | undefined,
    rawValue: string | boolean,
  ): void {
    const empty: Partial<CdvInvestigation> = {
      configuredDataElementId: cdeId,
      cdvListItemId: null,
      cdvDatetime: null,
      cdvShortVarchar: null,
      cdvNote: null,
      cdvInteger: null,
      cdvFloat: null,
      cdvYesNo: null,
    };
    const patch: Partial<CdvInvestigation> = { ...empty };
    const str = rawValue as string;
    switch (dataType) {
      case 'List Item':
        patch.cdvListItemId = str || null;
        break;
      case 'Datetime':
        patch.cdvDatetime = str || null;
        break;
      case 'Note':
        patch.cdvNote = str || null;
        break;
      case 'Short Varchar':
        patch.cdvShortVarchar = str || null;
        break;
      case 'Integer':
        patch.cdvInteger = str ? parseInt(str, 10) : null;
        break;
      case 'Float':
        patch.cdvFloat = str ? parseFloat(str) : null;
        break;
      case 'Yes/No':
        patch.cdvYesNo = rawValue as boolean;
        break;
    }
    this.cdeValues.update((m) => new Map(m).set(cdeId, patch));
  }

  private buildCdvInvestigations(): CdvInvestigation[] {
    return [...this.cdeValues().values()] as CdvInvestigation[];
  }

  /**
   * Full PUT payload; the PUT replaces everything, so send it all.
   * Case information, narrative, follow-up and CDEs come from local edits, so linking or
   * removing an incident also saves what the user typed (those flows reload the page).
   * Required case fields fall back to the saved values when cleared locally: Save blocks on
   * them, but link/remove are allowed and must not wipe them. The finding comes from the saved
   * record on purpose: sending a local finding would close the investigation.
   */
  private buildPayload(
    inv: InvestigationDetail,
    overrides: Partial<UpdateInvestigationPayload> = {},
  ): UpdateInvestigationPayload {
    const localCategories = this.localCategoryIds();
    return {
      id: inv.id,
      investigationNoticeDate: this.localNoticeDate() || inv.investigationNoticeDate,
      personSendingNoticeId: this.localPersonId() || inv.personSendingNoticeId,
      investigatingProgramId: this.localProgramId() || inv.investigatingProgramId,
      investigationNarrative: this.localNarrative().trim() || null,
      investigationFollowup: this.localFollowup().trim() || null,
      clientsInvolved: inv.clientsInvolved as number | null,
      finding: this.parseFinding(inv.finding),
      findingDate: inv.findingDate,
      investigationCategorizations: localCategories.length
        ? localCategories
        : inv.investigationCategorizations.map((c) => c.situationCategoryId),
      incidentInvestigations: inv.incidentInvestigations.map((i) => i.incidentId),
      cdvInvestigations: this.buildCdvInvestigations(),
      ...overrides,
    };
  }

  protected activeIncident = computed((): InvestigationLinkedIncident | null => {
    const inv = this.investigation();
    if (!inv?.incidentInvestigations?.length) return null;
    return inv.incidentInvestigations[this.activeIncidentIndex()] ?? null;
  });

  protected activeStage = computed((): IncidentStage => {
    const stage = this.activeIncident()?.currentStage;
    if (!stage) return 'new';
    if (stage === 'Completed') return 'closed';
    if (['A', 'B', 'C', 'D'].includes(stage)) return stage as IncidentStage;
    return 'new';
  });

  protected activeStageMeta = computed(() => STAGE_META[this.activeStage()]);
  protected activeStageIsTerminal = computed(
    () => this.activeStage() === 'closed' || this.activeStage() === 'new',
  );

  ngOnInit(): void {
    this.layout.set({
      title: 'Case File',
      breadcrumb: 'Investigation',
      showBackButton: true,
      switchPlaces: true,
    });

    // Angular reuses the component when only :id changes, so react to the param, not a snapshot.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.resetForInvestigation();
      this.loadInvestigation(params.get('id') ?? '');
    });
  }

  /** Clears per-investigation UI state before loading another one in the same component. */
  private resetForInvestigation(): void {
    this.loading.set(true);
    this.investigation.set(null);
    this.activeIncidentIndex.set(0);
    this.sidebarOpen.set(false);
    this.addingIncident.set(false);
    this.newIncidentId.set('');
    this.incidentOptions.set([]);
    this.incidentToRemove.set(null);
    this.showCloseConfirm.set(false);
    this.showReopenConfirm.set(false);
    this.closeAttempted.set(false);
    this.saveAttempted.set(false);
    this.cdeItems.set([]);
  }

  protected loadInvestigation(id: string): void {
    this.investigationService
      .getInvestigationById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.investigation.set(inv);
          this.layout.set({
            title: `${inv.investigationNumber ?? inv.id} - ${this.isClosed() ? 'Close' : 'Open'}`,
            breadcrumb: 'Investigation',
            showBackButton: true,
            switchPlaces: true,
          });
          // The date picker works with YYYY-MM-DDTHH:MM; the API returns full ISO strings.
          this.localNoticeDate.set(inv.investigationNoticeDate?.slice(0, 16) ?? '');
          this.localPersonId.set(inv.personSendingNoticeId ?? '');
          this.localProgramId.set(inv.investigatingProgramId ?? '');
          this.localCategoryIds.set(
            inv.investigationCategorizations.map((c) => c.situationCategoryId),
          );
          this.localNarrative.set(inv.investigationNarrative ?? '');
          this.localFollowup.set(inv.investigationFollowup ?? '');
          this.localFinding.set(this.parseFinding(inv.finding));
          this.localFindingDate.set(inv.findingDate);

          this.cdeValues.set(this.savedCdeValues(inv));

          this.loading.set(false);
          if (inv.investigatingProgramId) {
            this.fetchCdePrograms(inv.investigatingProgramId);
          }
          if (this.canEdit()) this.loadCaseOptions();
        },
        error: () => this.loading.set(false),
      });
  }

  /** Options for the case information fields. Only needed (and loaded once) when editable. */
  private loadCaseOptions(): void {
    if (this.caseOptionsLoaded) return;
    this.caseOptionsLoaded = true;
    this.caseOptionsLoading.set(true);

    // Persons use <app-person-select> (server search). Only investigating programs (type code II). No pageSize → the API returns them all.
    this.programService
      .getProgramTypes()
      .pipe(
        switchMap((res) => {
          const iiType = res.programTypes.find((t) => t.code === 'II');
          return iiType ? this.programService.getPrograms(undefined, iiType.id) : of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.programOptions.set(
            (res?.programs ?? []).map((p) => ({ value: p.id, label: p.name })),
          );
          this.caseOptionsLoading.set(false);
        },
        error: () => this.caseOptionsLoading.set(false),
      });

    this.categoryService
      .getSituationCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) =>
          this.categoryOptions.set(
            res.situationCategories.map((c) => ({ id: c.id, label: c.name })),
          ),
      });
  }

  private withSavedOption(
    options: SelectOption[],
    id: string | null | undefined,
    label: string | null | undefined,
  ): SelectOption[] {
    if (!id || options.some((o) => o.value === id)) return options;
    return [{ value: id, label: label ?? id }, ...options];
  }

  /**
   * Additional Data CDEs belong to the investigating program, so a new program means a
   * different set of fields: drop the old values and load the new program's CDEs.
   */
  protected onProgramChange(programId: string): void {
    if (programId === this.localProgramId()) return;
    this.localProgramId.set(programId);
    const inv = this.investigation();
    // Switching back to the saved program restores its saved values.
    this.cdeValues.set(
      inv && programId === inv.investigatingProgramId ? this.savedCdeValues(inv) : new Map(),
    );
    this.cdeItems.set([]);
    if (programId) this.fetchCdePrograms(programId);
  }

  private savedCdeValues(inv: InvestigationDetail): Map<string, Partial<CdvInvestigation>> {
    return new Map(inv.cdvInvestigations.map((cdv) => [cdv.configuredDataElementId, cdv]));
  }

  // Investigation_Result: 1 = Substantiated / Indicated, 0 = Unsubstantiated / Unfounded.
  // 0 is a real value, so never test it with truthiness.
  private parseFinding(value: string | number | null): number | null {
    if (value === null || value === '') return null;
    const n = Number(value);
    return n === 0 || n === 1 ? n : null;
  }

  private fetchCdePrograms(programId: string): void {
    this.dataElementService
      .getCdePrograms(programId, 0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (res) => this.cdeItems.set(res.items) });
  }

  protected startAddIncident(): void {
    if (!this.canEdit()) return;
    this.addingIncident.set(true);
    this.newIncidentId.set('');

    if (this.incidentOptions().length > 0) return;
    this.incidentPageIndex = 0;
    this.loadIncidentPage(0);
  }

  // GET /api/incidents always paginates and its text filter matches person names, not incident
  // numbers, so the picker pages through the list ("load more") and filters locally.
  private readonly INCIDENT_PAGE_SIZE = 50;
  private incidentPageIndex = 0;
  private incidentTotal = signal(0);
  private incidentsFetched = signal(0);
  protected incidentsHasMore = computed(() => this.incidentsFetched() < this.incidentTotal());

  protected onLoadMoreIncidents(): void {
    if (this.loadingIncidentOptions() || !this.incidentsHasMore()) return;
    this.loadIncidentPage(this.incidentPageIndex + 1);
  }

  private loadIncidentPage(pageIndex: number): void {
    this.loadingIncidentOptions.set(true);
    this.incidentService
      .getIncidents({ pageSize: this.INCIDENT_PAGE_SIZE, pageIndex })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const linked = new Set(
            this.investigation()?.incidentInvestigations.map((i) => i.incidentId) ?? [],
          );
          const page = res.incidents
            .filter((i) => !linked.has(i.id))
            .map((i) => ({
              value: i.id,
              label: i.incidentNumber ? `#${i.incidentNumber} — ${i.programName}` : i.id,
            }));
          this.incidentOptions.update((prev) => (pageIndex === 0 ? page : [...prev, ...page]));
          this.incidentPageIndex = pageIndex;
          this.incidentsFetched.update((n) => (pageIndex === 0 ? 0 : n) + res.incidents.length);
          this.incidentTotal.set(res.total);
          this.loadingIncidentOptions.set(false);
        },
        error: () => this.loadingIncidentOptions.set(false),
      });
  }

  protected cancelAddIncident(): void {
    this.addingIncident.set(false);
    this.newIncidentId.set('');
  }

  protected onSaveNewIncident(): void {
    const inv = this.investigation();
    const newId = this.newIncidentId();
    if (!inv || !newId || this.savingIncident() || !this.canEdit()) return;

    const payload = this.buildPayload(inv, {
      incidentInvestigations: [...inv.incidentInvestigations.map((i) => i.incidentId), newId],
    });
    if (!this.hasRequiredCaseInfo(payload)) return;

    this.savingIncident.set(true);
    this.investigationService
      .updateInvestigation(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.savingIncident.set(false);
          this.addingIncident.set(false);
          this.newIncidentId.set('');
          this.incidentOptions.set([]);
          this.toast.show('Incident linked successfully');
          this.loading.set(true);
          this.loadInvestigation(inv.id);
        },
        error: (err) => {
          this.toast.show(apiErrorMessage(err, 'Failed to link incident'), 'error');
          this.savingIncident.set(false);
        },
      });
  }

  /**
   * The API rejects any PUT without the required case fields ([Required] on UpdateInvestigationDto).
   * buildPayload falls back to saved values, so this only trips on older investigations saved
   * without them: ask the user to fill them in instead of sending a request that fails.
   */
  private hasRequiredCaseInfo(payload: UpdateInvestigationPayload): boolean {
    const ok = !!(
      payload.investigationNoticeDate &&
      payload.personSendingNoticeId &&
      payload.investigatingProgramId &&
      payload.investigationCategorizations.length
    );
    if (!ok) {
      this.saveAttempted.set(true);
      this.toast.show('Complete the required case information first', 'error');
      document
        .getElementById('case-information')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    return ok;
  }

  protected askRemoveIncident(incident: InvestigationLinkedIncident): void {
    if (!this.canRemoveIncident()) return;
    this.incidentToRemove.set(incident);
  }

  protected onConfirmRemoveIncident(): void {
    const inv = this.investigation();
    const target = this.incidentToRemove();
    this.incidentToRemove.set(null);
    if (!inv || !target || this.removingIncident() || !this.canRemoveIncident()) return;

    const payload = this.buildPayload(inv, {
      incidentInvestigations: inv.incidentInvestigations
        .filter((i) => i.incidentId !== target.incidentId)
        .map((i) => i.incidentId),
    });
    if (!this.hasRequiredCaseInfo(payload)) return;

    this.removingIncident.set(true);
    this.investigationService
      .updateInvestigation(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.removingIncident.set(false);
          this.activeIncidentIndex.set(0);
          this.incidentOptions.set([]);
          this.sidebarOpen.set(false);
          this.toast.show(`Incident #${target.incidentNumber} removed from the investigation`);
          this.loading.set(true);
          this.loadInvestigation(inv.id);
        },
        error: (err) => {
          this.toast.show(apiErrorMessage(err, 'Failed to remove incident'), 'error');
          this.removingIncident.set(false);
        },
      });
  }

  protected onSave(): void {
    if (!this.investigation() || this.saving() || !this.canEdit()) return;

    this.saveAttempted.set(true);
    if (!this.caseInfoValid()) {
      this.toast.show('Complete the required case information', 'error');
      document
        .getElementById('case-information')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (this.willClose()) {
      // Any saved finding closes the investigation for good, so it must be complete and confirmed.
      this.closeAttempted.set(true);
      if (!this.isComplete()) return;
      this.showCloseConfirm.set(true);
      return;
    }
    this.save();
  }

  protected onConfirmReopen(): void {
    this.showReopenConfirm.set(false);
    const inv = this.investigation();
    if (!inv || this.reopening() || !this.canReopen()) return;

    this.reopening.set(true);
    this.investigationService
      .reopenInvestigation(inv.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.reopening.set(false);
          this.toast.show('Investigation reopened');
          this.loadInvestigation(inv.id);
        },
        error: (err) => {
          this.reopening.set(false);
          this.toast.show(apiErrorMessage(err, 'Failed to reopen investigation'), 'error');
        },
      });
  }

  protected onConfirmClose(): void {
    this.showCloseConfirm.set(false);
    if (this.willClose() && this.isComplete()) this.save();
  }

  private save(): void {
    const inv = this.investigation();
    if (!inv || this.saving() || !this.canEdit()) return;
    const closing = this.willClose();

    const payload = this.buildPayload(inv, {
      finding: this.localFinding(),
      findingDate: this.localFindingDate(),
    });

    this.saving.set(true);
    this.investigationService
      .updateInvestigation(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.closeAttempted.set(false);
          this.saveAttempted.set(false);
          this.toast.show(closing ? 'Investigation finalized and closed' : 'Investigation saved');
          this.loadInvestigation(inv.id);
        },
        error: (err) => {
          this.toast.show(apiErrorMessage(err, 'Failed to save investigation'), 'error');
          this.saving.set(false);
        },
      });
  }

  protected goToIncident(incidentId: string, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/incidents', incidentId]);
  }

  protected formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
